import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Loader2, Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useT } from '../../context/LanguageContext';
import { payWithRazorpay, createSubscription, getPaymentConfig } from '../../services/paymentService';

/**
 * <RazorpayCheckoutButton
 *   amountEur={20}
 *   product="teacher_hour"
 *   label="1h with Elodie"
 *   mode="one-time" | "subscription"
 *   planKind="membership" | "club"     (only when mode='subscription')
 *   className="..."
 *   dataTestid="pay-teacher"
 * />
 */
export default function RazorpayCheckoutButton({
  amountEur,
  product,
  label,
  mode = 'one-time',
  planKind,
  className = '',
  dataTestid,
  disabled = false,
  children,
}) {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const t = useT();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    setError('');
    setLoading(true);
    try {
      if (mode === 'subscription') {
        const cfg = await getPaymentConfig();
        const planId = planKind === 'membership' ? cfg.membership_plan_id : cfg.club_plan_id;
        if (!planId) {
          setError(t({
            fr: "Les abonnements ne sont pas encore configurés. Veuillez utiliser le paiement mensuel.",
            en: 'Subscriptions are not yet configured. Please use monthly one-time payment.',
          }));
          setLoading(false);
          return;
        }
        const sub = await createSubscription({
          plan_kind: planKind,
          customer_email: user?.email,
          customer_name: user?.full_name,
        });
        if (sub.short_url) {
          window.location.href = sub.short_url;
          return;
        }
        navigate(`/payment/success?subscription_id=${encodeURIComponent(sub.subscription_id)}&product=${encodeURIComponent(product)}`);
        return;
      }

      const result = await payWithRazorpay({
        amountEur,
        currency,
        product,
        label,
        name: user?.full_name || '',
        email: user?.email || '',
        description: label,
      });
      const params = new URLSearchParams({
        order_id: result.order_id,
        payment_id: result.payment_id,
        product,
        label: label || '',
        amount: String(result.amount_display),
        currency: result.currency,
      });
      navigate(`/payment/success?${params.toString()}`);
    } catch (err) {
      // Cancel is expected — don't route to failure page for that.
      if (err?.message === 'Payment cancelled') {
        setError(t({ fr: 'Paiement annulé.', en: 'Payment cancelled.' }));
      } else {
        const msg = err?.message || 'Payment failed';
        setError(msg);
        navigate(`/payment/failure?error=${encodeURIComponent(msg)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const defaultLabel = mode === 'subscription'
    ? t({ fr: "S'abonner", en: 'Subscribe' })
    : t({ fr: 'Payer maintenant', en: 'Pay Now' });

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || loading}
        data-testid={dataTestid || 'razorpay-checkout-btn'}
        className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#1A2F4A] hover:bg-[#243a58] text-white font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed ${className}`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 text-[#D4AF37]" />}
        {children || defaultLabel}
      </button>
      {error && (
        <p className="text-[11px] text-red-600 mt-2 flex items-center gap-1" data-testid="payment-inline-error">
          <Lock className="w-3 h-3" /> {error}
        </p>
      )}
    </div>
  );
}
